import { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ChapbookDivider } from '../shared/components/ChapbookDivider.js';
import { useClockOffset } from '../shared/clock.js';
import { RolePlate } from '../shared/components/RolePlate.js';
import { SecretWordLabel } from '../shared/components/SecretWordLabel.js';
import { Spread } from '../shared/components/Spread.js';
import { Timer } from '../shared/components/Timer.js';
import { CastGrid } from './components/CastGrid.js';
import { ClueSlip } from '../shared/components/ClueSlip.js';
import { Lectern } from './components/Lectern.js';
import { MysteryFlipGrid } from '../shared/components/MysteryFlipGrid.js';
import { prefersReducedMotion } from '../preferences/reducedMotion.js';
import type { CluegivingPlayer, CluegivingView } from './types.js';
import './CluegivingPhase.css';

interface CluegivingPhaseProps {
  view: CluegivingView;
  /**
   * Invoked when the local viewer submits a clue from the lectern. In
   * production this dispatches `session.send('submitClue', { text })`; in
   * MockApp the parent stores the submitted text into the local view's
   * matching player so the slip transitions to its filled state.
   */
  onSubmitClue?: (text: string) => void;
  /**
   * When `true`, the entry inking beat plays on mount (mirrors
   * GhostMeeting's `transitioning` semantics, scaled to the lighter
   * clue-giving entrance). The caller (phase router in production, MockApp
   * in design-review) flips it back to `false` from `onTransitionComplete`.
   */
  transitioning?: boolean;
  onTransitionComplete?: () => void;
  /**
   * Mock-only escape hatch. When set, the V/F reveal cascade is replaced by
   * a static state with the first N cells revealed and the rest mystery —
   * used by `entry-cascade-midflight` to eyeball the half-revealed
   * geometry. Production never sets this. See `MysteryFlipGrid.revealedCount`.
   */
  revealedCountOverride?: number;
  /**
   * Cold-arrival flag from the phase router: true for the first render after a
   * reconnect / boot mid-clue-giving. Suppresses the per-slip ink-in so
   * already-submitted clues don't all animate in unison (handoff §6). The
   * entry beat is already gated by `transitioning` (false on a cold arrival),
   * so this only governs the clue slips. Default `false`.
   */
  justArrived?: boolean;
}

/**
 * The Clue-Giving destination. Verso (left) carries the action (timer +
 * prompt + cast band in turn order + lectern if you're the active speaker);
 * recto (right) carries the identity (role plate + divider + secret-word
 * sentence) — identical to GhostMeeting's recto, so the recto is the only
 * panel that stays put across the meeting → clue-giving boundary.
 *
 * Entry transition: when `transitioning` flips true → false, every verso
 * content element fades in over ~1200ms with staggered delays driven by
 * `CluegivingPhase.css` (cast first, then prompt + timer, then the
 * active-speaker lift lands, finally the lectern if you're up). The recto
 * is not animated — it carries through from the meeting unchanged.
 *
 * Mirrors GhostMeeting's pattern: `useLayoutEffect` (not `useEffect`) so the
 * `data-inking='yes'` flip lands in the SAME paint cycle as
 * `transitioning → false`. With `useEffect` there was a paint gap where
 * `data-transitioning='no'` but `data-inking='no'`, revealing content at
 * opacity 1 for one frame before the keyframe restarted from 0.
 */
export function CluegivingPhase({
  view,
  onSubmitClue,
  transitioning = false,
  onTransitionComplete,
  revealedCountOverride,
  justArrived = false,
}: CluegivingPhaseProps): JSX.Element {
  const { t } = useTranslation();

  const meetingPov: 'village' | 'ghost' = view.pov === 'ghost' ? 'ghost' : 'village';
  /* V/F (villager + fool) share the same entry transition — both saw the
     mystery grid in ghost-meeting and have never been shown the playing
     order. The flip-reveal is for them. Ghost POV skips it (they voted on
     the order, they already know who's first). */
  const usesFlipReveal = view.pov !== 'ghost';

  /* Away (disconnected-but-alive) ids, threaded into the flip grid's back-face
     AvatarFrames — MysteryFlipGrid types its players as the structural
     SeatPlayer, which doesn't carry `connected`. The Ghost-POV CastGrid below
     reads `player.connected` directly, so it needs no equivalent. */
  const awayPlayerIds = useMemo(
    () => new Set(view.players.filter((p) => !p.connected).map((p) => p.id)),
    [view.players],
  );

  const isLocalActiveSpeaker = view.currentClueGiverId === view.meId;
  const activeSpeaker = view.currentClueGiverId
    ? view.players.find((p) => p.id === view.currentClueGiverId) ?? null
    : null;

  /* State machine for the entry beat. Two paths:
       V/F:   transitioning → revealing → fadingPrompt → inking → idle
       Ghost: transitioning → inking → idle  (no flip; just a fade-in)
     `transitioning` is driven externally (the parent flips it false after
     the interstitial hold). `revealing`, `fadingPrompt`, and `inking`
     cascade locally. The `fadingPrompt` phase (V/F only) holds the old
     interstitial copy while it eases out — without it, the React text
     swap at inking start cuts hard from "playing order has been decided"
     to the active-speaker copy and the eye reads it as a flip-flop. */
  const [revealing, setRevealing] = useState(false);
  const [fadingPrompt, setFadingPrompt] = useState(false);
  const [inking, setInking] = useState(false);

  /* The verso prompt holds the interstitial line "The playing order has
     been decided." through the interstitial hold, the V/F flip cascade,
     AND the prompt fade-out beat. Only when inking begins (after the old
     copy has eased to opacity 0) does the prompt resolve to its phase
     copy — and the ink-in animation fades the new text up from 0, in
     lock-step with the timer. */
  let promptText: string;
  if (transitioning || revealing || fadingPrompt) {
    promptText = t('cluegiving.prompt.interstitial');
  } else if (view.currentClueGiverId === null) {
    promptText = t('cluegiving.prompt.complete');
  } else if (isLocalActiveSpeaker) {
    promptText = t('cluegiving.prompt.mine');
  } else {
    promptText = t('cluegiving.prompt.other', {
      name: activeSpeaker?.name ?? '',
    });
  }

  /* The hand-to-hand pass (turn-indication brief, docs/mockups/cluegiving-
     turn-05-leans.html). When the active speaker changes mid-phase, the
     outgoing settle plays immediately while the incoming lift, the local
     lean, and the lectern entrance all wait out a 400ms dead-point (CSS
     transition/animation delays). The prompt rides the same window: it
     fades out (280ms via `data-prompt-passing`), holds, then swaps text in
     lock-step with the visuals landing. During the entry choreography the
     interstitial machinery owns the prompt, so the swap snaps instead —
     as it does under reduced motion, where the pass collapses entirely. */
  const inEntryBeat = transitioning || revealing || fadingPrompt || inking;
  const [shownPrompt, setShownPrompt] = useState(promptText);
  const [promptPassing, setPromptPassing] = useState(false);
  useEffect(() => {
    if (promptText === shownPrompt) return undefined;
    const reducedMotion = prefersReducedMotion();
    if (inEntryBeat || reducedMotion) {
      setShownPrompt(promptText);
      setPromptPassing(false);
      return undefined;
    }
    setPromptPassing(true);
    const handle = window.setTimeout(() => {
      setShownPrompt(promptText);
      setPromptPassing(false);
    }, 400);
    return () => window.clearTimeout(handle);
  }, [promptText, shownPrompt, inEntryBeat]);
  /* During entry beats the interstitial machinery swaps the prompt text in
     the same paint as its data-attribute flips (useLayoutEffect above);
     `shownPrompt` syncs one passive-effect paint later, which would flash
     stale copy into the ink-in. Render the live text through entry, the
     lagged text only for mid-phase passes. */
  const renderedPrompt = inEntryBeat ? promptText : shownPrompt;

  /* The clock presses (escalation tier): the local speaker is up,
     unsubmitted, and the phase clock has run inside 9s. Neutral-ink
     pressure only — the Timer alone carries the sub-5s Oxblood. Mirrors
     Timer's skew-corrected phaseEndsAt arithmetic at a coarse 250ms poll;
     purely visual, so drift against the Timer's rAF loop is imperceptible. */
  const clockOffset = useClockOffset();
  const [pressing, setPressing] = useState(false);
  useEffect(() => {
    const endsAt = view.phaseEndsAt;
    if (!isLocalActiveSpeaker || endsAt === null) {
      setPressing(false);
      return undefined;
    }
    const compute = () => setPressing(endsAt - (Date.now() + clockOffset) <= 9000);
    compute();
    const handle = window.setInterval(compute, 250);
    return () => window.clearInterval(handle);
  }, [isLocalActiveSpeaker, view.phaseEndsAt, clockOffset]);

  /* Cold-Arrival Rule for the lean: on a reconnect/boot directly into the
     local player's own turn, the leaned posture must be present at first
     paint, not animated in. One unarmed frame suppresses the spread's
     transform transition (CSS), then arms it so later handoffs animate.
     Captured once at mount, like ClueSlip's suppressInk. */
  const [leanArmed, setLeanArmed] = useState(!justArrived);
  useEffect(() => {
    if (leanArmed) return undefined;
    const raf = requestAnimationFrame(() => setLeanArmed(true));
    return () => cancelAnimationFrame(raf);
    // Deps intentionally empty: arms exactly once, at mount.
  }, []);

  /* Latest cast size, read by the cascade-completion effect below at arm
     time. A ref, NOT an effect dep: with `view.players.length` in the deps,
     a hard player delete mid-cascade re-ran the effect, the cleanup killed
     the armed timers, and the re-run saw no transitioning edge (the prev
     ref was already overwritten) — `revealing` stuck true for the rest of
     the phase, holding the timer + Lectern at opacity 0 for every
     subsequent speaker. */
  const playersLengthRef = useRef(view.players.length);
  playersLengthRef.current = view.players.length;

  const prevTransitioningRef = useRef(transitioning);
  useLayoutEffect(() => {
    const wasTransitioning = prevTransitioningRef.current;
    prevTransitioningRef.current = transitioning;
    if (wasTransitioning && !transitioning) {
      if (usesFlipReveal) setRevealing(true);
      else setInking(true);
    }
  }, [transitioning, usesFlipReveal]);

  /* Cascade completion (V/F only) — armed while `revealing` is true, and
     deliberately keyed on nothing else: the flip cards have already been
     dealt, so the schedule rides out on the cast it started with even if
     the roster changes underneath. `revealing` always resolves.

     Three timing constants ride on top of the cascade end:
       beat (100ms)    — pure pause after the last card lands. Lets
                         the eye register the cards in place before
                         anything else changes.
       fadeoutDuration — must match the prompt fade-out keyframe in
         (300ms)        CluegivingPhase.css; while this elapses the
                         prompt holds the old interstitial copy and
                         eases to opacity 0.
       inkingStarts    — prompt text swaps + fade-in + timer ink-in
                         fire together. New state inks in as one
                         coordinated beat.
     Keep the 900ms per-card flip in sync with MysteryFlipGrid.css.
       3 players → 1260ms cascade + 100 + 300 = 1660ms to inking
       5 players → 1620ms cascade + 100 + 300 = 2020ms to inking
       8 players → 2160ms cascade + 100 + 300 = 2560ms to inking */
  useEffect(() => {
    if (!revealing) return undefined;
    const lastCardEnds = 180 * Math.max(0, playersLengthRef.current - 1) + 900;
    const beat = 100;
    const fadeoutDuration = 300;
    const fadeoutStartsAt = lastCardEnds + beat;
    const inkingStartsAt = fadeoutStartsAt + fadeoutDuration;
    const fadeoutTimer = window.setTimeout(() => {
      setFadingPrompt(true);
    }, fadeoutStartsAt);
    const inkingTimer = window.setTimeout(() => {
      setRevealing(false);
      setFadingPrompt(false);
      setInking(true);
    }, inkingStartsAt);
    return () => {
      window.clearTimeout(fadeoutTimer);
      window.clearTimeout(inkingTimer);
    };
  }, [revealing]);

  /* Inking phase teardown. Different durations for V/F (cast is already
     revealed; only timer/lift/lectern need to arrive) vs Ghost (cast
     fades in, then timer/lift/lectern). Both phases end by clearing
     `inking` so any data-attribute-driven CSS rules go idle. */
  useEffect(() => {
    if (!inking) return undefined;
    /* V/F post-flip: 1300ms (timer 0-600ms, lift 200-700ms, lectern 600-1100ms).
       Ghost: 2200ms (cast fade 0-600ms, then timer 400-1000ms, lift 700-1200ms,
       lectern 1500-2000ms). The V/F flip already consumed the cast-reveal
       beat, so this phase is purely "spotlight + pen extends." */
    const inkDuration = usesFlipReveal ? 1300 : 2200;
    const handle = window.setTimeout(() => setInking(false), inkDuration);
    return () => window.clearTimeout(handle);
  }, [inking, usesFlipReveal]);

  /* If the parent mounts us with `transitioning=true`, hold the interstitial
     line for ~2500ms before firing the completion callback. 2500ms = 800ms
     fade-in + 1700ms of pure hold — long enough that "The playing order has
     been decided." lands as a deliberate inscription, not a wipe. Reduced
     motion collapses the hold to ~300ms so keyboard reviewers aren't left
     waiting. */
  useEffect(() => {
    if (!transitioning || !onTransitionComplete) return undefined;
    const reducedMotion = prefersReducedMotion();
    const holdMs = reducedMotion ? 300 : 2500;
    const handle = window.setTimeout(() => onTransitionComplete(), holdMs);
    return () => window.clearTimeout(handle);
  }, [transitioning, onTransitionComplete]);

  /* The cast region renders either the shared mystery/flip grid (V/F) or
     the regular cast grid (Ghost). For V/F, the cards stay mounted post-
     reveal — the back face (AvatarFrame) is what's visible after the
     rotation completes. `revealed` toggles when `transitioning` flips
     false (after the interstitial hold); MysteryFlipGrid's CSS staggers
     the per-card flip via `transition-delay`. The lookup-by-id in the
     footer renderer is required because MysteryFlipGrid hands back the
     structural `SeatPlayer` shape, not the phase-specific CluegivingPlayer
     that carries `.clue`. */
  const playersById = view.players.reduce<Map<string, CluegivingPlayer>>(
    (acc, player) => {
      acc.set(player.id, player);
      return acc;
    },
    new Map(),
  );
  const castRegion = usesFlipReveal ? (
    <MysteryFlipGrid
      mode="revealing"
      players={view.players}
      awayPlayerIds={awayPlayerIds}
      revealed={!transitioning}
      activePlayerId={view.currentClueGiverId}
      activeSpeakerLabel={(player) =>
        t('cluegiving.cast.activeSpeaker', { name: player.name })
      }
      ariaLabel={t('cluegiving.cast.ariaLabel')}
      {...(revealedCountOverride !== undefined
        ? { revealedCount: revealedCountOverride }
        : {})}
      renderCellFooter={(player) => {
        const full = playersById.get(player.id);
        if (!full) return null;
        return <ClueSlip clue={full.clue} ownerName={full.name} suppressInk={justArrived} testId={`clue-slip-${full.id}`} />;
      }}
    />
  ) : (
    <CastGrid
      players={view.players}
      ghostIds={view.ghostIds}
      meId={view.meId}
      currentClueGiverId={view.currentClueGiverId}
      justArrived={justArrived}
    />
  );

  return (
    <div
      className="susdle-cluegiving"
      data-inking={inking ? 'yes' : 'no'}
      data-revealing={revealing ? 'yes' : 'no'}
      data-fading-prompt={fadingPrompt ? 'yes' : 'no'}
      data-transitioning={transitioning ? 'yes' : 'no'}
      data-pov={view.pov === 'ghost' ? 'ghost' : 'village'}
      data-mine={isLocalActiveSpeaker ? 'yes' : 'no'}
      data-press={pressing ? 'yes' : 'no'}
      data-prompt-passing={promptPassing ? 'yes' : 'no'}
      data-lean-armed={leanArmed ? 'yes' : 'no'}
    >
      <h1 className="susdle-sr-only">{t('cluegiving.ariaLabel')}</h1>
      <Spread
        pov={meetingPov}
        ariaLabel={t('cluegiving.ariaLabel')}
        verso={
          <div className="susdle-cluegiving-verso">
            <div className="susdle-cluegiving-verso__timer">
              <Timer
                phaseEndsAt={view.phaseEndsAt}
                durationMs={view.durationMs}
              />
            </div>
            <p className="susdle-cluegiving-verso__prompt">{renderedPrompt}</p>
            {castRegion}
            {isLocalActiveSpeaker && onSubmitClue ? (
              <Lectern onSubmit={onSubmitClue} suppressEntry={justArrived} />
            ) : null}
          </div>
        }
        recto={
          <div className="susdle-cluegiving-recto">
            <RolePlate
              preamble={t('meeting.rolePlate.preamble')}
              roleName={t(
                view.pov === 'ghost'
                  ? 'meeting.rolePlate.ghost'
                  : 'meeting.rolePlate.villager',
              )}
            />
            <ChapbookDivider />
            <div className="susdle-cluegiving-recto__secret">
              {view.secret.mode === 'word' ? (
                <SecretWordLabel mode="word" word={view.secret.value} />
              ) : (
                <SecretWordLabel mode="length" length={view.secret.value} />
              )}
            </div>
          </div>
        }
      />
    </div>
  );
}
